/**
 * API Service Layer
 * -----------------
 * Fetches data from the backend ONLY.
 * No fallback to demo data.
 */

import {
  sendOTP,
  verifyOTP,
  resendOTP,
  checkUserExists,
  logout,
  adminLogin,
  adminLogout,
  isAuthenticated,
  getCurrentUser,
  isAdmin as checkIsAdmin,
  handleAuthSuccess,
} from './authService';

const resolveBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) return envBase.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    return isLocalhost ? 'http://localhost:8000/api' : '/api';
  }

  return 'http://localhost:8000/api';
};

const BASE_URL = resolveBaseUrl();

/** Helper: fetch from backend only (no fallback) */
const CLIENT_CACHE_TTL_MS = 30 * 1000;
const FETCH_TIMEOUT_MS = 10 * 1000;
const clientCache = new Map();
const safeFetch = async (url) => {
  const now = Date.now();
  const cached = clientCache.get(url);
  if (cached && cached.expiresAt > now) return cached.promise;

  const promise = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json.success && json.data ? json.data : json;
    } catch (err) {
      // Never persist failures in cache, so future calls can retry.
      clientCache.delete(url);
      console.error(`API fetch failed (${url}):`, err.message);
      return [];
    } finally {
      clearTimeout(timeout);
    }
  })();

  clientCache.set(url, {
    promise,
    expiresAt: now + CLIENT_CACHE_TTL_MS,
  });

  return promise;
};

// Global Home Data Fetcher
const HOME_DATA_CACHE_TTL_MS = 60 * 1000;
const homeDataPromiseCache = new Map();
export const getHomeData = async (city = 'Mumbai') => {
  const url = `${BASE_URL}/public/home-data?city=${encodeURIComponent(city)}`;
  const now = Date.now();
  const cached = homeDataPromiseCache.get(url);
  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }

  if (!cached || cached.expiresAt <= now) {
    const promise = safeFetch(url).then((data) => {
      if (!data || Array.isArray(data)) {
        homeDataPromiseCache.delete(url);
        return null;
      }
      return data;
    }).catch(() => {
      homeDataPromiseCache.delete(url);
      return null;
    });

    homeDataPromiseCache.set(url, {
      promise,
      expiresAt: now + HOME_DATA_CACHE_TTL_MS,
    });
  }

  return homeDataPromiseCache.get(url).promise;
};

export const prefetchHomeData = (city = 'Mumbai') => getHomeData(city);

/* ───────── HOME PAGE ───────── */

export const getPropertyListings = async (city = 'Mumbai') => {
  const data = await getHomeData(city);
  if (data && data.listings) return data.listings;
  return safeFetch(`${BASE_URL}/public/listings?city=${city}`);
};

export const getTopDevelopers = async (city = 'Mumbai') => {
  const data = await getHomeData(city);
  if (data && data.builders) return data.builders;
  return safeFetch(`${BASE_URL}/public/builders?city=${city}`);
};

export const getLocalities = async (city = 'Mumbai') => {
  const data = await getHomeData(city);
  if (data && data.localities) return data.localities;
  return safeFetch(`${BASE_URL}/public/localities?city=${city}`);
};

export const getPopularLocalities = async (city = 'Mumbai', limit = 8, propertyType = '') => {
  // If we have homeData, extract from localities
  const data = await getHomeData(city);
  if (data && data.localities) {
    const cityKey = Object.keys(data.localities).find(k => k.toLowerCase() === city.toLowerCase());
    if (cityKey && data.localities[cityKey]) {
      return data.localities[cityKey].slice(0, limit);
    }
  }
  const params = new URLSearchParams({ city, limit: limit.toString() });
  if (propertyType) params.set('propertyType', propertyType);
  return safeFetch(`${BASE_URL}/public/popular-localities?${params.toString()}`);
};

export const getReviews = async () => {
  const data = await getHomeData();
  if (data && data.reviews) return data.reviews;
  return safeFetch(`${BASE_URL}/settings/reviews`);
};

export const getStats = async () => {
  return safeFetch(`${BASE_URL}/public/stats`);
};

export const getPossessionStages = async () => {
  const data = await getHomeData();
  if (data && data.processStages) return data.processStages;
  return safeFetch(`${BASE_URL}/settings/possession-stages`);
};

export const getAdvantages = async () => {
  const data = await getHomeData();
  if (data && data.advantages) return data.advantages;
  return safeFetch(`${BASE_URL}/settings/advantages`);
};

export const getMediaCoverage = async () => {
  const data = await getHomeData();
  if (data && data.mediaCoverage) return data.mediaCoverage;
  return safeFetch(`${BASE_URL}/settings/media-coverage`);
};

/* ───────── BUILDER PAGE ───────── */

export const getBuilderBySlug = async (slug) => {
  try {
    const res = await fetch(`${BASE_URL}/public/builders/${slug}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    throw new Error("No data");
  } catch (err) {
    console.error(`Builder fetch failed:`, err.message);
    return null;
  }
};

export const getBuilderProjects = async (slug, filters = {}) => {
  const builder = await getBuilderBySlug(slug);
  if (!builder) return [];
  let projects = builder.projects || [];
  if (filters.hideSoldOut) projects = projects.filter((p) => !p.isSoldOut);
  if (filters.sortBy === 'price-low') projects = [...projects].sort((a, b) => (a.priceNumeric || 0) - (b.priceNumeric || 0));
  else if (filters.sortBy === 'price-high') projects = [...projects].sort((a, b) => (b.priceNumeric || 0) - (a.priceNumeric || 0));
  return projects;
};

/* ───────── LOCALITY PAGE ───────── */

export const getLocalityBySlug = async (city, slug) => {
  try {
    const res = await fetch(`${BASE_URL}/public/localities/${slug}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    throw new Error("No data");
  } catch (err) {
    console.error(`Locality fetch failed:`, err.message);
    return null;
  }
};

/* ───────── MAP VIEW - City-wide listings ───────── */
export const getMapListings = async (city = 'Mumbai') => {
  try {
    const res = await fetch(`${BASE_URL}/public/map-listings?city=${encodeURIComponent(city)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    throw new Error("No data");
  } catch (err) {
    console.error(`Map listings fetch failed:`, err.message);
    return { projects: [], totalProjects: 0, localities: [], city };
  }
};

/* ───────── CITY PAGE ───────── */
export const getCityListings = async (city = 'Mumbai', filters = {}) => {
  try {
    const params = new URLSearchParams({ city });
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.litigationFree) params.set('litigationFree', 'true');
    if (filters.hideSoldOut) params.set('hideSoldOut', 'true');
    if (filters.topDeveloper) params.set('topDeveloper', 'true');
    const res = await fetch(`${BASE_URL}/public/city-listings?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    throw new Error("No data");
  } catch (err) {
    console.error(`City listings fetch failed:`, err.message);
    return { projects: [], totalProjects: 0, localities: [], builders: [], city };
  }
};

export const getLocalityProjects = async (city, slug, filters = {}) => {
  const locality = await getLocalityBySlug(city, slug);
  if (!locality) return [];
  let projects = locality.projects || [];
  if (filters.hideSoldOut) projects = projects.filter((p) => !p.isSoldOut);
  return projects;
};

/* ───────── PROJECT DETAIL PAGE ───────── */

export const getProjectBySlug = async (slug) => {
  try {
    const res = await fetch(`${BASE_URL}/public/listings/${slug}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    throw new Error("No data");
  } catch (err) {
    console.error(`Project fetch failed:`, err.message);
    return null;
  }
};

export const getRelatedProjects = async (slug) => {
  try {
    const res = await fetch(`${BASE_URL}/public/listings/${slug}/related`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.success && json.data) return json.data;
    return [];
  } catch (err) {
    console.error(`Related projects fetch failed:`, err.message);
    return [];
  }
};

/* ───────── AUTH ───────── */

// Export OTP auth functions
export { sendOTP, verifyOTP, resendOTP, checkUserExists };

// Export admin auth functions
export { adminLogin, adminLogout };

// Export auth utilities
export { logout, isAuthenticated, getCurrentUser, checkIsAdmin, handleAuthSuccess };

// Legacy getUserProfile function (kept for backward compatibility)
export const getUserProfile = async () => {
  const token = localStorage.getItem('userToken');
  if (!token) return null;
  try {
    const res = await fetch(`${BASE_URL}/user/profile`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Not authenticated');
    const json = await res.json();
    return json.user || json;
  } catch { return null; }
};

/* ───────── CONTACT / LEAD FORMS ───────── */

export const submitCallMeForm = async (data) => {
  try {
    const res = await fetch(`${BASE_URL}/callback-requests`, {
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json;
  } catch (err) {
    console.error('Call me form error:', err);
    return { success: false, message: 'Failed to submit request' };
  }
};

export const submitSiteVisitForm = async (data) => {
  try {
    const res = await fetch(`${BASE_URL}/public/contact`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, message: `Site visit request` }),
    });
    return res.json();
  } catch {
    return { success: true, message: 'Site visit booked successfully!' };
  }
};

export const submitSearchQuery = async (query) => {
  return safeFetch(`${BASE_URL}/public/search?q=${encodeURIComponent(query)}`);
};

/* ───────── CONTACT PAGE ───────── */

export const getContactOffices = async () => {
  return safeFetch(`${BASE_URL}/contact/offices`);
};

export const submitContactForm = async (data) => {
  try {
    const res = await fetch(`${BASE_URL}/contact/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to submit');
    return json;
  } catch (err) {
    console.error('Contact form submission error:', err);
    throw err;
  }
};

/* ───────── BOOKMARKS ───────── */

// Get all user bookmarks
export const getUserBookmarks = async () => {
  const token = localStorage.getItem('userToken');
  if (!token) throw new Error('Not authenticated');
  
  try {
    const res = await fetch(`${BASE_URL}/bookmarks`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    
    // If user not found (404), it means the token is stale/invalid
    if (res.status === 404 && json.message === 'User not found') {
      console.warn('⚠️ Stale token detected - User not found in database. Clearing auth...');
      localStorage.removeItem('userToken');
      localStorage.removeItem('user');
      throw new Error('Session expired - Please login again');
    }
    
    if (!res.ok) throw new Error(json.message || 'Failed to fetch bookmarks');
    return json;
  } catch (err) {
    console.error('Get bookmarks error:', err);
    throw err;
  }
};

// Toggle bookmark (add if not bookmarked, remove if bookmarked)
export const toggleBookmark = async (propertyId) => {
  const token = localStorage.getItem('userToken');
  if (!token) throw new Error('Not authenticated');
  
  try {
    const res = await fetch(`${BASE_URL}/bookmarks/${propertyId}/toggle`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to toggle bookmark');
    return json;
  } catch (err) {
    console.error('Toggle bookmark error:', err);
    throw err;
  }
};

// Check if property is bookmarked
export const checkBookmark = async (propertyId) => {
  const token = localStorage.getItem('userToken');
  if (!token) return { isBookmarked: false };
  
  try {
    const res = await fetch(`${BASE_URL}/bookmarks/${propertyId}/check`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) return { isBookmarked: false };
    return json;
  } catch (err) {
    console.error('Check bookmark error:', err);
    return { isBookmarked: false };
  }
};
